import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, useCallback, useEffect } from 'react';
import BlogPostSingle from '../../components/blog/BlogPostSingle';
import { ICallToActionProps } from '../../components/common/blocks/CallToAction';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import TeamMemberBox, {
  ITeamMemberBoxProps,
} from '../../components/team-members/TeamMemberBox';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IBlogPage,
  IBlogPost,
  IBlogPostsResult,
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  ILinkWithIconComponent,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateBlogPostSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  getAlternateLinksForBlogPost,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getTranslation,
  textToSlug,
} from '../../shared/useUtils';
import { NextPageWithLayout } from '.././_app';

export interface IBlogPostPageProps extends ILayoutPageProps {
  post: IBlogPost;
  lastUpdatedLabel: string;
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  author: {
    fullname: string;
    jobTitle: string;
    biography: string;
    socialMediaIcons: ILinkWithIconComponent[];
    avatarUrl: string;
    slug: string;
  };
  tableOfContentsTitle: string;
  authorPrefix: string;
  publishedDatePrefix: string;
  callToAction: ICallToActionComponent;
}

const BlogPostPage: NextPageWithLayout<IBlogPostPageProps> = (
  props: IBlogPostPageProps
) => {
  const router = useRouter();
  const setHtmlAdditionalAttributes = useCallback(() => {
    document.documentElement.setAttribute('itemscope', '');
    document.documentElement.setAttribute(
      'itemtype',
      'https://schema.org/Article'
    );
  }, []);

  const blogPostProps = {
    ...props.post,
    content: props.post.body,
    lastUpdatedLabel: props.lastUpdatedLabel,
    authorPrefix: props.authorPrefix,
    authorPath: props.author.slug,
    publishedDatePrefix: props.publishedDatePrefix,
    tableOfContentsTitle: props.tableOfContentsTitle,
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  const mapSocialMediaIcons = (icons: ILinkWithIconComponent[]) => {
    return icons.map((socialMediaIcon) => ({
      iconUrl:
        getImageSrcWithFallback(socialMediaIcon.icon, 'full') ||
        '/assets/images/home/map-image.png',
      link: socialMediaIcon.link,
    }));
  };

  const teamMemberBoxProps: ITeamMemberBoxProps = {
    fullname: props.author.fullname,
    jobTitle: props.author.jobTitle,
    biography: props.author.biography,
    socialMediaIcons: mapSocialMediaIcons(props.author.socialMediaIcons || []),
    imageUrl: props.author.avatarUrl || '',
    slug: props.author.slug,
  };

  const callToActionProps: ICallToActionProps = {
    title: props.callToAction.title,
    buttons: props.callToAction.buttons,
  };

  const ContactComponent = dynamic(
    () => import('../../components/contact-us/Contact')
  );
  const CallToActionComponent = dynamic(
    () => import('../../components/common/blocks/CallToAction')
  );

  useEffect(() => {
    setHtmlAdditionalAttributes();
  }, [setHtmlAdditionalAttributes]);

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}

        {/* Open graph */}
        <meta property="og:title" content={props.post.title} />
        <meta
          property="og:url"
          content={getCanonicalLink(router.locale || 'en', props.meta.slug)}
        />
        <meta property="og:description" content={props.meta.description} />
        <meta
          property="og:image"
          content={
            props.post.image?.formats?.medium?.url || props.post.image?.url
          }
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={props.post.title} />
        <meta
          name="twitter:url"
          content={getCanonicalLink(router.locale || 'en', props.meta.slug)}
        />
        <meta name="twitter:description" content={props.meta.description} />
        <meta
          name="twitter:image"
          content={
            props.post.image?.formats?.medium?.url || props.post.image?.url
          }
        />

        {/* Linkedin & Pinterest */}
        <meta itemProp="name" content={props.post.title} />
        <meta itemProp="description" content={props.meta.description} />
        <meta
          itemProp="image"
          content={
            props.post.image?.formats?.medium?.url || props.post.image?.url
          }
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section is-narrow">
        <div className="page-content-block is-text-align-justified-mobile">
          <BlogPostSingle {...blogPostProps} />
        </div>
      </div>

      <div className="page-content-section is-narrow">
        <div className="page-content-block">
          <TeamMemberBox {...teamMemberBoxProps} />
        </div>
      </div>

      <div className="page-content-section is-narrower">
        <div className="page-content-block">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      {(props.callToAction as any).text !== 'n/a' && (
        <div className="page-content-section is-full-width">
          <div className="page-content-block" id="services">
            <CallToActionComponent {...callToActionProps} />
          </div>
        </div>
      )}
    </div>
  );
};

BlogPostPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticPaths(context: any) {
  let paths: any[] = [];

  for (const locale of context.locales) {
    const urlParams = new URLSearchParams({
      locale,
      'pagination[start]': '0',
      'pagination[limit]': '-1',
      disableLocalizations: 'true',
    });
    const blogPostsResponse: any = await fetchStrapi<any>(
      `/blog-posts/published`,
      urlParams,
      buildApiKey
    );

    const localizedPaths = blogPostsResponse.results.map((item: IBlogPost) => ({
      params: {
        slug: (item.id as number).toString(),
      },
      locale: item.locale,
    }));
    paths = [...paths, ...localizedPaths];
  }

  return { paths, fallback: false };
}

export async function getStaticProps(
  context: any
): Promise<{ props: IBlogPostPageProps }> {
  const appRoutes = (await getRoutes()).filter((x) =>
    x.original?.includes('/blog')
  );

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'contactUsCallToAction',
    'populate[2]': 'contactUsCallToAction.image',
    'populate[3]': 'contactUsForm',
    'populate[4]': 'contactUsFormSuccessMessage',
    'populate[5]': 'contactUsFormErrorMessage',
    'populate[6]': 'tableOfContentsTitle',
    'populate[7]': 'callToAction',
    'populate[8]': 'callToAction.buttons',
    'populate[9]': 'authorPrefix',
    'populate[10]': 'publishedDatePrefix',
  });

  const publishedPostUrlParams = new URLSearchParams({
    locale: context.locale,
    disableLocalizations: 'true',
    'populate[1]': 'author',
    'populate[2]': 'author.avatar',
    'populate[3]': 'author.jobTitle',
    'populate[4]': 'author.socialMediaIcons',
    'populate[5]': 'author.socialMediaIcons.icon',
    'populate[6]': 'author.biography',
    'populate[7]': 'author.meta',
    'populate[8]': 'callToAction',
    'populate[9]': 'callToAction.buttons',
    'populate[10]': 'image',
  });

  const page = (
    await fetchStrapi<any>(`/blog-page`, pageUrlParams, buildApiKey)
  ).data.attributes as IBlogPage;
  const postResult: IBlogPostsResult = await fetchStrapi<IBlogPostsResult>(
    `/blog-posts/published/${context.params.slug}`,
    publishedPostUrlParams,
    buildApiKey
  );
  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);
  const alternativeLinks = getAlternateLinksForBlogPost(
    context.locale || 'en',
    context.params.slug,
    appRoutes
  );
  const canonicalLink = getCanonicalLink(
    context.locale || 'en',
    postResult.results[0].slug || textToSlug(postResult.results[0].title)
  );
  const post = postResult.results[0];
  const commonTranslations = await getCommonTranslations();
  const callToAction =
    post.callToAction ||
    (getTranslation(
      page.callToAction,
      context.locale
    ) as ICallToActionComponent);

  if (post.image) {
    post.image.formats = {
      medium: post.image.formats?.medium,
      large: post.image.formats?.large,
    };
  }

  // Populate breadcrumbs
  if (post.breadcrumbAncestors && post.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = post.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = post.title;

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );

  // Prepare author for schema
  const schemaAuthor =
    post.author && post.author.meta
      ? {
          fullname: post.author.fullname || '',
          jobTitle: getTranslation(post.author.jobTitle, context.locale)
            .text as string,
          profileSlug: getTranslation(post.author.meta, context.locale).slug,
        }
      : null;

  const blogPostSchema = generateBlogPostSchema(post, schemaAuthor);
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema, ...blogPostSchema],
  };

  if (post.jsonldAddiontalSchema) {
    pageSchema.nodes.push(post.jsonldAddiontalSchema);
  }

  const author = {
    fullname: post.author?.fullname || '',
    jobTitle: getTranslation(post.author?.jobTitle, context.locale)
      .text as string,
    biography: getTranslation(post.author?.biography, context.locale)
      .text as string,
    socialMediaIcons: post.author?.socialMediaIcons || [],
    avatarUrl:
      getImageSrcWithFallback(post.author?.avatar, 'full') ||
      '/assets/images/home/map-image.png',
    slug: getTranslation(post.author?.meta, context.locale).slug || '',
  };

  const props: IBlogPostPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta: {
      title: postResult.results[0].title,
      description: postResult.results[0].metaDescription || '',
      language: context.locale,
      slug:
        postResult.results[0].slug || textToSlug(postResult.results[0].title),
      keywords: postResult.results[0].metaKeywords,
    },
    post: postResult.results[0],
    lastUpdatedLabel: getTranslation(
      commonTranslations.lastUpdatedLabel,
      context.locale
    ).text as string,
    contactMethods,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormSuccessMessage: getTranslation(
      page.contactUsFormSuccessMessage,
      context.locale
    ).text as string,
    contactUsFormErrorMessage: getTranslation(
      page.contactUsFormErrorMessage,
      context.locale
    ).text as string,
    alternativeLinks,
    canonicalLink,
    author,
    tableOfContentsTitle: getTranslation(
      page.tableOfContentsTitle,
      context.locale
    ).text as string,
    authorPrefix: getTranslation(page.authorPrefix, context.locale)
      .text as string,
    publishedDatePrefix: getTranslation(
      page.publishedDatePrefix,
      context.locale
    ).text as string,
    callToAction,
  };

  return { props };
}

export default BlogPostPage;
